import axios from 'axios';
import { getActiveRole, getDeviceId, clearAllSessions } from '../utils/sessionKeys';

const API_URL = '/api/v1';

// Create axios instance with default config.
// withCredentials: true ensures httpOnly cookies are sent with every request.
// No Bearer header needed — the backend reads the access_token cookie.
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Silent refresh state — shared across concurrent requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

// Response interceptor: silently refresh on 401, then retry the original request.
// The refresh endpoint uses the refresh_token httpOnly cookie (path-restricted
// to /api/v1/auth/refresh) — the browser sends it automatically.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401 and not on the refresh endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        // Another refresh is in flight — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // POST to refresh endpoint — browser sends refresh_token cookie automatically
        await axios.post(`${API_URL}/auth/refresh`, {
          device_id: getDeviceId(),
        }, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          }
        });

        // Backend set new access_token + refresh_token cookies in the response.
        // No localStorage update needed — cookies are automatic.
        processQueue(null);

        // Retry the original request (browser sends new cookie)
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearAllSessions();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Non-401 errors: use existing error handler
    try {
      const { handleApiError } = require('./errorHandler');
      handleApiError(error);
    } catch (e) {
      // errorHandler might not be available
    }

    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (credentials) => {
    return api.post('/login', credentials);
  },
  register: (userData) => {
    return api.post('/register', userData);
  },
  getProfile: () => {
    return api.get('/profile');
  },
  updateProfile: (userData) => {
    return api.put('/profile', userData);
  },
  logout: () => {
    return api.post('/logout');
  },
  googleLogin: (credential) => {
    return api.post('/auth/google', { credential });
  }
};

// Astrologer services
export const astrologerService = {
  getAstrologers: (params) => {
    return api.get('/astrologers', { params }).then(response => {
      console.log('Astrologers response:', response.data);
      return response;
    });
  },
  getAstrologer: (id) => {
    return api.get(`/astrologers/${id}`);
  },
  getOnlineAstrologers: () => {
    return api.get('/astrologers/online');
  },
  rateAstrologer: (id, rating) => {
    return api.post(`/astrologers/${id}/rate`, rating);
  }
};

// Consultation services
export const consultationService = {
  createConsultation: (data) => {
    console.log('Creating consultation with data:', data);
    return api.post('/consultations', data).then(response => {
      console.log('Consultation creation response:', response.data);
      return response;
    }).catch(error => {
      console.error('Consultation creation error:', error.response?.data || error);
      throw error;
    });
  },
  getConsultations: (params) => {
    return api.get('/consultations', { params });
  },
  getConsultation: (id) => {
    return api.get(`/consultations/${id}`);
  },
  updateConsultation: (id, data) => {
    return api.put(`/consultations/${id}`, data);
  },
  getMessages: (id) => {
    return api.get(`/consultations/${id}/messages`);
  },
  sendMessage: (id, message) => {
    return api.post(`/consultations/${id}/messages`, { content: message });
  },
  getHistory: (params) => {
    return api.get('/consultations/history', { params });
  },
  rateConsultation: (id, data) => {
    return api.post(`/consultations/${id}/rating`, data);
  }
};

// Kundli services
export const kundliService = {
  generateKundli: (data) => {
    return api.post('/kundlis', data);
  },
  getKundlis: () => {
    return api.get('/kundlis');
  },
  getKundli: (id) => {
    return api.get(`/kundlis/${id}`);
  }
};

// Free Kundli service (public, no auth required)
export const freeKundliService = {
  generateChart: (data) => api.post('/free-kundli', data),
  listAyanamsas: () => api.get('/kundli/ayanamsas'),
  calculateMatching: (data) => api.post('/kundli/matching', data),
  getTodayPanchangam: (params) => api.get('/panchangam/today', { params }),
};

// KalaSakthi V2 — unified scoring engine (public, no auth)
export const kalaSakthiService = {
  computeV2: (data) => api.post('/kalasakthi/v2', data),
  computeToday: (data) => api.post('/kalasakthi/today', data),
  computeTimeline: (data) => api.post('/kalasakthi/timeline', data),
};

// Daily Balance (KalaShakti) services
export const dailyBalanceService = {
  getDailyBalance: (data) => api.post('/daily-balance', data),
  getPlanetDetail: (data, planet) => api.post(`/daily-balance/detail?planet=${planet}`, data),
  getHourlyTimeline: (data) => api.post('/daily-balance/hourly', data),
  getWeeklyForecast: (data) => api.post('/daily-balance/weekly', data),
};

// Vedic Astrology services (vedic_astro microservice via /vedic/v1/)
export const vedicService = {
  getDailyTimings: (params) => axios.get('/vedic/v1/daily-timings', { params }),
  getHora: (params) => axios.get('/vedic/v1/hora', { params }),
  getTaraBala: (params) => axios.get('/vedic/v1/tara-bala', { params }),
  getAbhijitMuhurta: (params) => axios.get('/vedic/v1/abhijit-muhurta', { params }),
  checkMuhurta: (data) => axios.post('/vedic/v1/muhurta/check', data),
  calculate10Porutham: (data) => axios.post('/vedic/v1/matching/porutham', data),
  interpretDasha: (data) => axios.post('/vedic/v1/dasha/interpretation', data),
  analyzeEventTiming: (data) => axios.post('/vedic/v1/event-timing', data),
  analyzeYogas: (data) => axios.post('/vedic/v1/yoga/analysis', data),
  analyzeTransits: (data) => axios.post('/vedic/v1/transit/analysis', data),
};

// KP (Krishnamurti Paddhati) Stellar System services
export const kpService = {
  generateChart: (data) => api.post('/kp/chart', data),
  generateHorary: (data) => api.post('/kp/horary', data),
  predictEvent: (data) => api.post('/kp/predict', data),
  getRulingPlanets: (params) => api.get('/kp/ruling-planets', { params }),
  getTable249: () => api.get('/kp/table-249'),
};

// Panchangam services (P4 endpoints, public, no auth required)
export const panchangService = {
  getDaily: (params) => api.get('/panchang/daily', { params }),
  compare: (params) => api.get('/panchang/compare', { params }),
};

// Utility services
export const utilService = {
  getTimezone: (lat, lon) => api.get('/timezone', { params: { lat, lon } }),
};

// Horoscope services
export const horoscopeService = {
  getHoroscope: (sign, type = 'daily') => {
    return api.get(`/horoscopes/${sign}`, { params: { type } });
  }
};

// Saved horoscope profiles (authenticated users/astrologers)
export const savedHoroscopeService = {
  list: () => api.get('/horoscopes/saved'),
  create: (data) => api.post('/horoscopes/saved', data),
  remove: (id) => api.delete(`/horoscopes/saved/${id}`),
};

// Product services
export const productService = {
  getProducts: (params) => {
    return api.get('/products', { params });
  },
  getProduct: (id) => {
    return api.get(`/products/${id}`);
  }
};

// Order services
export const orderService = {
  createOrder: (data) => {
    return api.post('/orders', data);
  },
  getOrders: () => {
    return api.get('/orders');
  },
  getOrder: (id) => {
    return api.get(`/orders/${id}`);
  }
};

// Admin Invitation services
export const adminInvitationService = {
  // Get all invitations with optional filters
  getInvitations: (filters = {}) => {
    // Make a clean copy of filters removing any temporary properties like _refresh
    // These are used only to trigger refreshes but shouldn't be sent to the API
    const { _refresh, ...apiFilters } = filters;
    
    // If status is 'all', remove it from the params as the backend should return all invitations
    if (apiFilters.status === 'all') {
      delete apiFilters.status;
    }
    
    console.log('Sending API filters to backend:', apiFilters);
    return api.get('/admin/invitations', { params: apiFilters });
  },
  
  // Get a single invitation by ID
  getInvitation: (id) => {
    return api.get(`/admin/invitations/${id}`);
  },
  
  // Create a new invitation
  createInvitation: (data) => {
    return api.post('/admin/invitations', data);
  },
  
  // Delete an invitation
  deleteInvitation: (id) => {
    return api.delete(`/admin/invitations/${id}`);
  },
  
  // Resend an invitation
  resendInvitation: (id) => {
    return api.put(`/admin/invitations/${id}/resend`);
  }
};

// Astrologer Registration services
export const astrologerRegistrationService = {
  // Verify invitation token
  verifyInvitation: (token) => {
    return api.get(`/invitations/verify/${token}`);
  },

  // Register astrologer using invitation token
  registerAstrologer: (data) => {
    return api.post('/register/astrologer', data);
  }
};

// Astrologer Dashboard services (for logged-in astrologers)
export const astrologerDashboardService = {
  // Get astrologer's bookings (users who booked with them)
  getMyBookings: (params = {}) => {
    return api.get('/astrologer/bookings', { params });
  },

  // Get active consultations
  getActiveConsultations: () => {
    return api.get('/astrologer/bookings', { params: { status: 'confirmed' } });
  },

  // Get dashboard summary (stats, earnings, next consultation)
  getSummary: () => {
    return api.get('/astrologer/dashboard/summary');
  },

  // Get upcoming consultations
  getUpcomingConsultations: (params = {}) => {
    return api.get('/astrologer/dashboard/consultations/upcoming', { params });
  },

  // Get recent messages
  getRecentMessages: (params = {}) => {
    return api.get('/astrologer/dashboard/messages/recent', { params });
  },

  // Get earnings data
  getEarnings: (params = {}) => {
    return api.get('/astrologer/dashboard/earnings', { params });
  }
};

// User Dashboard services
export const userDashboardService = {
  // Get dashboard summary (stats, horoscope, next consultation)
  getSummary: () => {
    return api.get('/user/dashboard/summary');
  },

  // Get upcoming consultations
  getUpcomingConsultations: (params = {}) => {
    return api.get('/user/dashboard/consultations/upcoming', { params });
  },

  // Get consultation history
  getConsultationHistory: (params = {}) => {
    return api.get('/user/dashboard/consultations/history', { params });
  },

  // Get recent activity feed
  getRecentActivity: (params = {}) => {
    return api.get('/user/dashboard/activity', { params });
  },

  // Get user horoscope
  getUserHoroscope: (type = 'daily') => {
    return api.get('/user/dashboard/horoscope', { params: { type } });
  }
};

export default api;
